import { FunctionComponent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApiCall } from '@/hooks/useApiCall';
import { useUser } from '@/modules/users/contexts/UserContext';
import { UserType } from '@/modules/users/api/users/users.contracts';
import ErrorLoadingDataAlert from '@/components/feedback/ErrorLoadingDataAlert';
import { useTranslation } from '@/lib';
import { TabbedPageLayout } from '@/modules/application/layouts/TabbedPageLayout';
import { TabbedPageLayoutBody } from '@/modules/application/components/TabbedPageLayoutBody';
import FilterBar from '@/components/filterbar/FilterBar';
import TextFilter from '@/components/filterbar/filters/TextFilter';
import useQueryParamsFilters from '@/hooks/useQueryParamFilters';
import { CacheKey } from '@/providers/cache-provider/cache-key.enum';
import { FilterValues } from '@/components/filterbar/FilterBarContext';
import GuestLocationsGrid from '../components/GuestLocationsGrid';
import { BalancesAtLocationModel } from '@/modules/balances/models/BalanceAtLocationModel';
import { balanceService } from '@/modules/balances/api/balances/balance.service';

const GuestLocationsPage: FunctionComponent = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { filters, setFiltersToUrl } = useQueryParamsFilters(
    {
      searchText: searchParams.get('searchText') ?? '',
    },
    CacheKey.LOCATION_FILTERS,
  );

  const { data, isLoading, isError, fetchData, setApiCallArg } = useApiCall<BalancesAtLocationModel[]>(() =>
    balanceService.getBalancesForLocations({ query: filters.current.searchText ?? '' }),
  );

  useEffect(() => {
    if (user?.type !== UserType.GUEST) {
      navigate('/locations');
      return;
    }

    fetchData()
      .then(() => {
        // If only one location, navigate directly to it
        if (data && data.length === 1) {
          navigate(`/app/guest-locations/${data[0].dto.locationId}`);
        }
      })
      .catch(() => {
        // Error handling is managed by the useApiCall hook
      });
  }, [user, navigate, fetchData]);

  useEffect(() => {
    // Update API call when search text changes
    setApiCallArg(() => balanceService.getBalancesForLocations({ query: filters.current.searchText ?? '' }));
  }, [filters.current.searchText, setApiCallArg]);

  const handleFiltersChanged = (newFilters: FilterValues) => {
    setFiltersToUrl(newFilters);
  };

  if (user?.type !== UserType.GUEST) {
    return null;
  }

  if (isError) {
    return <ErrorLoadingDataAlert />;
  }

  return (
    <TabbedPageLayout>
      <TabbedPageLayoutBody>
        <FilterBar onSearch={handleFiltersChanged} initialFilterValues={filters.current} isLoading={isLoading}>
          <div className="w-44">
            <TextFilter label={t('search')} />
          </div>
        </FilterBar>

        <GuestLocationsGrid
          data={data || []}
          isLoading={isLoading}
          onLocationSelected={(location: BalancesAtLocationModel) => navigate(`/app/guest-locations/${location.dto.locationId}`)}
        />
      </TabbedPageLayoutBody>
    </TabbedPageLayout>
  );
};

export default GuestLocationsPage;
